// Ambulation Zone — RedString
// The conspiracy red-string board. THE DO-NOT-PULL-THE-THREAD BOARD.
//
// Derived from Pinboard: a brick/corkboard surface where the existing
// artportfolio photos are pinned with red/green/yellow tacks, mixed with
// taped index notes. Everything is tied together with taut RED yarn rendered
// as an absolutely-positioned SVG layer behind the pins. Each pinned item is
// draggable; while dragging we recompute ONLY the connected string endpoints
// (rafThrottle — never a timer). Click empty brick to tack up a new sticky;
// stickies persist in localStorage under ambulate_redstring.
//
// PERF: no requestAnimationFrame loop, no setInterval. The SVG strings are
// pure React state, recomputed only on drag move (coalesced to one frame via
// rafThrottle from the shared util). Off-drag the board is completely idle.

import * as React from "react";
import "./redstring.css";
import { rafThrottle, prefersReducedMotion } from "../_shared/sketchFrame";

import data from "./strings.json";

// VERIFIED present in src/images/artportfolio (listed before importing):
// skeyeSkape.png, deepHeavy.png, lookatem.png, holeHandDream.png
import skeyeSkape from "../../../images/artportfolio/skeyeSkape-web.png";
import deepHeavy from "../../../images/artportfolio/deepHeavy-web.png";
import lookatem from "../../../images/artportfolio/lookatem-web.png";
import holeHandDream from "../../../images/artportfolio/holeHandDream-web.png";

import redtack from "../../../images/redtack.png";
import greentack from "../../../images/greentack.png";
import yellowtack from "../../../images/yellowtack.png";
import notepaper from "../../../images/notepapertaped.png";

const IMG = { skeyeSkape, deepHeavy, lookatem, holeHandDream };
const TACK = { red: redtack, green: greentack, yellow: yellowtack };
const TACKS = [redtack, greentack, yellowtack];

const STORAGE_KEY = "ambulate_redstring";

// A pinned art image that fails to load must NEVER leave a silent blank box.
// Hide just that <img> and tag the card so CSS collapses the empty well.
function handleArtError(e) {
    const img = e.currentTarget;
    img.style.display = "none";
    const card = img.closest(".rs-node");
    if (card) card.classList.add("rs-missing");
}
// A missing tack / taped-note texture is purely decorative — just hide it.
function handleDecoError(e) {
    e.currentTarget.style.display = "none";
}

// Read any user-added stickies the visitor tacked up on a previous visit.
function loadStickies() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        // Keep only well-formed stickies so a corrupted entry can't render a
        // broken tack (TACKS[NaN]) or an "undefined%" position.
        return parsed.filter(
            (n) =>
                n &&
                typeof n.id === "string" &&
                typeof n.x === "number" &&
                typeof n.y === "number" &&
                typeof n.text === "string"
        );
    } catch (err) {
        return [];
    }
}

class RedString extends React.Component {
    constructor(props) {
        super(props);

        this.surfaceRef = React.createRef();

        // Seed node positions are percentages; we keep a live position map keyed
        // by node id so dragging just mutates one entry and the strings follow.
        const positions = {};
        data.nodes.forEach((n) => {
            positions[n.id] = { x: n.x, y: n.y };
        });

        this.state = {
            positions,                 // { id: {x%, y%} } for seeded evidence
            stickies: loadStickies(),  // user-added notes [{id,x,y,rot,tack,text}]
            // endpoint pixel coords per string, recomputed during drag:
            // [{ x1,y1,x2,y2 }] — null until first measure (CSS % anchors hold
            // the line visually fine; we compute exact px lazily on drag).
            segments: [],
        };

        this.reduced = prefersReducedMotion();

        // Drag bookkeeping (refs, not state — no re-render per move).
        this.dragId = null;
        this.dragKind = null;     // 'node' | 'sticky'
        this.pointerId = null;

        // Bind first so the throttled wrapper captures the bound method —
        // the rAF callback invokes fn.apply(listenerThis,...), and listenerThis
        // is `window`, so handleMove MUST already be bound to this instance.
        this.handleDown = this.handleDown.bind(this);
        this.handleMove = this.handleMove.bind(this);
        this.handleUp = this.handleUp.bind(this);
        this.handleSurfaceClick = this.handleSurfaceClick.bind(this);
        this.recomputeSegments = this.recomputeSegments.bind(this);
        this.handleArtLoad = this.handleArtLoad.bind(this);
        this.handleArtErrorRS = this.handleArtErrorRS.bind(this);

        // Coalesce noisy pointermove to one recompute per animation frame.
        this.onMoveThrottled = rafThrottle(this.handleMove);

        // Pending rAF handles for the first-layout measure, so we can cancel
        // them on unmount and never leave a frame callback dangling.
        this._raf1 = null;
        this._raf2 = null;
    }

    componentDidMount() {
        // The pinned art is <img decoding="async">, so on the very first paint
        // those nodes have not been laid out at their final size yet — measuring
        // now would anchor the yarn to zero-size boxes (or skip them entirely),
        // which is why the lines used to stay hidden until a click/drag/resize
        // forced a fresh measure. Instead, measure AFTER the browser has had a
        // real layout pass: schedule across two animation frames (layout +
        // paint settle), and also re-measure on window load and as each art
        // image finishes loading. No timer, no polling loop.
        this.scheduleRecompute();

        this._onWindowLoad = () => this.scheduleRecompute();
        // If the page already finished loading, this listener simply never
        // fires — the rAF + per-image onLoad paths already cover that case.
        window.addEventListener("load", this._onWindowLoad);

        // Reflow the strings if the board is resized (no polling timer).
        this._onResize = rafThrottle(this.recomputeSegments);
        window.addEventListener("resize", this._onResize);
    }

    // Measure once the first layout/paint has actually happened. Double rAF:
    // the first frame lets React's committed DOM lay out, the second runs after
    // the subsequent paint so getBoundingClientRect reflects final geometry.
    scheduleRecompute() {
        if (this._raf1) cancelAnimationFrame(this._raf1);
        if (this._raf2) cancelAnimationFrame(this._raf2);
        this._raf1 = requestAnimationFrame(() => {
            this._raf1 = null;
            this._raf2 = requestAnimationFrame(() => {
                this._raf2 = null;
                this.recomputeSegments();
            });
        });
    }

    // Each pinned photo settles its real dimensions only once decoded; remeasure
    // then so the yarn snaps onto the final pin centers even before any click.
    handleArtLoad() {
        this.scheduleRecompute();
    }

    // A failed photo collapses its node to the fixed .rs-missing placeholder
    // size, which shifts that pin's center — hide the broken <img> (shared
    // helper) then remeasure so the yarn re-anchors to the new geometry.
    handleArtErrorRS(e) {
        handleArtError(e);
        this.scheduleRecompute();
    }

    componentWillUnmount() {
        // Tear down every listener + drop any pending coalesced frame.
        if (this.onMoveThrottled) this.onMoveThrottled.cancel();
        if (this._raf1) cancelAnimationFrame(this._raf1);
        if (this._raf2) cancelAnimationFrame(this._raf2);
        if (this._onWindowLoad) window.removeEventListener("load", this._onWindowLoad);
        if (this._onResize) {
            this._onResize.cancel();
            window.removeEventListener("resize", this._onResize);
        }
        window.removeEventListener("pointermove", this.onMoveThrottled);
        window.removeEventListener("pointerup", this.handleUp);
        window.removeEventListener("pointercancel", this.handleUp);
    }

    // Resolve a node id to its center pixel coords inside the surface.
    centerOf(id) {
        const surface = this.surfaceRef.current;
        if (!surface) return null;
        const el = surface.querySelector('[data-node="' + id + '"]');
        if (!el) return null;
        const s = surface.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        return {
            x: r.left - s.left + r.width / 2,
            y: r.top - s.top + r.height / 2,
        };
    }

    // Rebuild the pixel endpoints for the red strings. Called on mount, on
    // resize, and on each drag frame — NEVER on a timer.
    recomputeSegments() {
        const segs = [];
        for (let i = 0; i < data.strings.length; i++) {
            const [a, b] = data.strings[i];
            const pa = this.centerOf(a);
            const pb = this.centerOf(b);
            if (pa && pb) segs.push({ x1: pa.x, y1: pa.y, x2: pb.x, y2: pb.y });
        }
        this.setState({ segments: segs });
    }

    handleDown(e, id, kind) {
        // Only left button / touch / pen starts a drag.
        if (e.button != null && e.button !== 0) return;
        e.stopPropagation(); // don't let the surface-click add a sticky

        const surface = this.surfaceRef.current;
        if (!surface) return;
        const pos =
            kind === "node"
                ? this.state.positions[id]
                : this.state.stickies.find((n) => n.id === id);
        // A corrupted sticky (missing x/y) must not throw and wedge dragging.
        if (!pos || typeof pos.x !== "number" || typeof pos.y !== "number") return;

        this.dragId = id;
        this.dragKind = kind;
        this.pointerId = e.pointerId;

        const s = surface.getBoundingClientRect();
        // Pixel offset from the item's anchored center to the pointer, so the
        // item doesn't jump under the cursor on grab.
        const cx = (pos.x / 100) * s.width;
        const cy = (pos.y / 100) * s.height;
        this.grabDX = e.clientX - s.left - cx;
        this.grabDY = e.clientY - s.top - cy;

        window.addEventListener("pointermove", this.onMoveThrottled);
        window.addEventListener("pointerup", this.handleUp);
        window.addEventListener("pointercancel", this.handleUp);
    }

    handleMove(e) {
        if (this.dragId == null) return;
        const surface = this.surfaceRef.current;
        if (!surface) return;
        const s = surface.getBoundingClientRect();

        let nx = ((e.clientX - s.left - this.grabDX) / s.width) * 100;
        let ny = ((e.clientY - s.top - this.grabDY) / s.height) * 100;
        // Keep the pin on the board.
        nx = Math.max(2, Math.min(98, nx));
        ny = Math.max(2, Math.min(98, ny));

        if (this.dragKind === "node") {
            this.setState(
                (prev) => ({
                    positions: { ...prev.positions, [this.dragId]: { x: nx, y: ny } },
                }),
                // Recompute ONLY after the moved position is committed so the
                // connected string endpoints follow exactly.
                this.recomputeSegments
            );
        } else {
            this.setState(
                (prev) => ({
                    stickies: prev.stickies.map((n) =>
                        n.id === this.dragId ? { ...n, x: nx, y: ny } : n
                    ),
                }),
                this.recomputeSegments
            );
        }
    }

    handleUp() {
        const wasSticky = this.dragKind === "sticky";
        this.dragId = null;
        this.dragKind = null;
        if (this.onMoveThrottled) this.onMoveThrottled.cancel();
        window.removeEventListener("pointermove", this.onMoveThrottled);
        window.removeEventListener("pointerup", this.handleUp);
        window.removeEventListener("pointercancel", this.handleUp);
        // A dragged sticky moved — persist its new resting spot. Read the
        // freshest state inside a setState callback: the final drag-move
        // setState may still be pending/batched, so this.state.stickies here
        // can lag a frame. The empty updater flushes, then we persist truth.
        if (wasSticky) {
            this.setState(null, () => this.persistStickies(this.state.stickies));
        }
    }

    persistStickies(stickies) {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stickies));
        } catch (err) {
            /* private mode / quota — fail silent, board still works */
        }
    }

    // Click an empty spot on the brick to tack up a fresh sticky note.
    handleSurfaceClick(e) {
        // Ignore clicks that landed on a pinned item (those start drags).
        if (e.target.closest(".rs-node")) return;
        const surface = this.surfaceRef.current;
        const s = surface.getBoundingClientRect();
        const x = ((e.clientX - s.left) / s.width) * 100;
        const y = ((e.clientY - s.top) / s.height) * 100;

        const text = window.prompt("Scrawl on the evidence:", "");
        if (text == null) return; // cancelled
        const trimmed = text.trim();
        if (!trimmed) return;

        const sticky = {
            id: "sticky-" + Date.now().toString(36),
            x,
            y,
            rot: ((Date.now() % 14) - 7), // -7..6 deg, stable per note
            tackIdx: Date.now() % 3,
            text: trimmed.slice(0, 80),
        };
        const next = this.state.stickies.concat(sticky);
        this.setState({ stickies: next }, this.recomputeSegments);
        this.persistStickies(next);
    }

    removeSticky(e, id) {
        e.stopPropagation();
        const next = this.state.stickies.filter((n) => n.id !== id);
        this.setState({ stickies: next });
        this.persistStickies(next);
    }

    render() {
        const { positions, stickies, segments } = this.state;

        return (
            <div className="redstring-frame">
                <div
                    className="redstring-surface"
                    ref={this.surfaceRef}
                    onClick={this.handleSurfaceClick}
                >
                    {/* RED YARN LAYER — absolutely-positioned SVG behind the pins.
                        Endpoints are exact pixels recomputed on drag. */}
                    <svg className="rs-strings" preserveAspectRatio="none">
                        {segments.map((seg, i) => (
                            <line
                                key={i}
                                className="rs-string"
                                x1={seg.x1}
                                y1={seg.y1}
                                x2={seg.x2}
                                y2={seg.y2}
                            />
                        ))}
                    </svg>

                    {/* SEEDED EVIDENCE — pinned photos + taped index notes */}
                    {data.nodes.map((n) => {
                        const pos = positions[n.id];
                        const style = {
                            left: pos.x + "%",
                            top: pos.y + "%",
                            transform:
                                "translate(-50%, -50%) rotate(" + n.rot + "deg)",
                        };
                        if (n.type === "art") {
                            return (
                                <div
                                    key={n.id}
                                    data-node={n.id}
                                    className="rs-node rs-art-node"
                                    style={style}
                                    onPointerDown={(e) => this.handleDown(e, n.id, "node")}
                                >
                                    <img
                                        className="rs-art"
                                        src={IMG[n.img]}
                                        alt={n.label}
                                        draggable={false}
                                        decoding="async"
                                        onLoad={this.handleArtLoad}
                                        onError={this.handleArtErrorRS}
                                    />
                                    <img
                                        className="rs-tack"
                                        src={TACK[n.tack]}
                                        alt=""
                                        draggable={false}
                                        decoding="async"
                                        onError={handleDecoError}
                                    />
                                    <span className="rs-label">{n.label}</span>
                                </div>
                            );
                        }
                        // taped index note
                        return (
                            <div
                                key={n.id}
                                data-node={n.id}
                                className="rs-node rs-note-node"
                                style={style}
                                onPointerDown={(e) => this.handleDown(e, n.id, "node")}
                            >
                                <img
                                    className="rs-note-paper"
                                    src={notepaper}
                                    alt="taped note"
                                    draggable={false}
                                    decoding="async"
                                    onError={handleDecoError}
                                />
                                <span className="rs-note-text">{n.text}</span>
                            </div>
                        );
                    })}

                    {/* USER STICKIES — tacked up by clicking empty brick */}
                    {stickies.map((n) => {
                        const rot = typeof n.rot === "number" ? n.rot : 0;
                        const tackIdx =
                            typeof n.tackIdx === "number" ? n.tackIdx : 0;
                        const style = {
                            left: n.x + "%",
                            top: n.y + "%",
                            transform:
                                "translate(-50%, -50%) rotate(" + rot + "deg)",
                        };
                        return (
                            <div
                                key={n.id}
                                data-node={n.id}
                                className="rs-node rs-sticky"
                                style={style}
                                onPointerDown={(e) => this.handleDown(e, n.id, "sticky")}
                            >
                                <img
                                    className="rs-sticky-tack"
                                    src={TACKS[tackIdx % 3]}
                                    alt=""
                                    draggable={false}
                                    decoding="async"
                                    onError={handleDecoError}
                                />
                                <span className="rs-sticky-text">{n.text}</span>
                                <button
                                    type="button"
                                    className="rs-sticky-x"
                                    title="rip it down"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => this.removeSticky(e, n.id)}
                                >
                                    x
                                </button>
                            </div>
                        );
                    })}

                    <div className="rs-watermark">EVIDENCE // DO NOT PULL THE THREAD</div>
                </div>
                <p className="rs-hint">
                    drag the evidence &middot; click bare brick to tack a note
                </p>
            </div>
        );
    }
}

export default RedString;
